import React, { useState, useEffect } from 'react'
import { Box, Flex, Button, TextInput, NumberInput, Select, MultiSelect, SimpleGrid, Group, Text, Image, Paper } from '@mantine/core'
import { useForm } from '@mantine/form'
import { createCategoryRule, updateCategoryRule } from '../../../../services/category-rules'
import { getCategorysList, getDetailCategory } from '../../../../services/category'
import { getAllHscodes } from '../../../../services/hscodes'
import { getAllCountryList } from '../../../../services/location'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconX } from '@tabler/icons-react'
import NoImage from '../../../../assets/img/images/no-images.png'

const FormCategoryRule = ({ dataDetail, onCloseModal, reloadList }) => {
    const [loading, setLoading] = useState(false)
    const [catLvl1, setCatLvl1] = useState([])
    const [catLvl2, setCatLvl2] = useState([])
    const [catLvl3, setCatLvl3] = useState([])
    const [hscodes, setHscodes] = useState([])
    const [countries, setCountries] = useState([])
    const [selectedCountry, setSelectedCountry] = useState(null)

    const form = useForm({
        initialValues: {
            description: dataDetail ? dataDetail.description : '',
            weight: dataDetail ? dataDetail.weight : 0,
            weight_unit: dataDetail ? dataDetail.weight_unit : 'GR',
            qty_cbm: dataDetail ? dataDetail.qty_cbm : 1,
            category_lvl1: null,
            category_lvl2: null,
            category_id: null,
            hscodes: dataDetail && dataDetail.hscodes && dataDetail.hscodes.length > 0 ? dataDetail.hscodes.map(h => String(h.id)) : [],
        },
        validate: {
            description: (value) => (value.trim().length === 0 ? 'Description is required' : null),
            category_id: (value) => (!value ? 'Category is required' : null),
        },
    })

    const initCategoryHierarchy = async () => {
        if (!dataDetail || !dataDetail.categories || dataDetail.categories.length === 0) return;
        setLoading(true);
        try {
            const catLvl3Id = String(dataDetail.categories[0].id);
            const cat3Res = await getDetailCategory(catLvl3Id);
            const catLvl2Id = String(cat3Res.data.attributes.parent_id);
            
            const cat2Res = await getDetailCategory(catLvl2Id);
            const catLvl1Id = String(cat2Res.data.attributes.parent_id);
            
            await fetchCategoriesLvl2(catLvl1Id);
            await fetchCategoriesLvl3(catLvl2Id);
            
            form.setValues({
                ...form.values,
                category_lvl1: catLvl1Id,
                category_lvl2: catLvl2Id,
                category_id: catLvl3Id,
            });
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategoriesLvl1()
        fetchCountries()
        if (dataDetail) {
            initCategoryHierarchy()
        }
    }, [])

    useEffect(() => {
        fetchHscodes()
    }, [selectedCountry])

    const fetchCountries = async () => {
        try {
            const res = await getAllCountryList({ 'page[limit]': 1000 })
            if (res.data) {
                setCountries(res.data.map(c => ({ value: String(c.id), label: c.attributes.name })))
            }
        } catch (error) {
            console.error(error)
        }
    }

    const fetchHscodes = async () => {
        try {
            const params = { 'page[limit]': 1000 }
            if (selectedCountry) {
                params['filter[country_id][is]'] = selectedCountry
            }
            const res = await getAllHscodes(params)
            if (res.data) {
                setHscodes(res.data.map(h => ({ value: String(h.id), label: `${h.attributes.code} - ${h.attributes.description}` })))
            }
        } catch (error) {
            console.error(error)
        }
    }

    const fetchCategoriesLvl1 = async () => {
        try {
            const res = await getCategorysList({ 'filter[level][is]': 1, 'page[limit]': 100 })
            setCatLvl1(res.data.data.map(c => ({ value: String(c.attributes.id), label: c.attributes.name, image: c.attributes.image })))
        } catch (e) {
            console.error(e)
        }
    }

    const fetchCategoriesLvl2 = async (parentId) => {
        if (!parentId) return
        try {
            const res = await getCategorysList({ 'filter[parent_id][is]': parentId, 'page[limit]': 100 })
            setCatLvl2(res.data.data.map(c => ({ value: String(c.attributes.id), label: c.attributes.name, image: c.attributes.image })))
        } catch (e) {
            console.error(e)
        }
    }

    const fetchCategoriesLvl3 = async (parentId) => {
        if (!parentId) return
        try {
            const res = await getCategorysList({ 'filter[parent_id][is]': parentId, 'page[limit]': 100 })
            setCatLvl3(res.data.data.map(c => ({ value: String(c.attributes.id), label: c.attributes.name, image: c.attributes.image })))
        } catch (e) {
            console.error(e)
        }
    }

    // Remove the individual useEffects for category watching
    // We already handle this beautifully with the onChange of Selects

    const renderSelectOption = ({ option }) => (
        <Group flex="1" gap="xs">
            <Paper shadow="sm" w={32} withBorder radius='md' style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <Image src={option.image || NoImage} w={30} h={30} radius="sm" fallbackSrc={NoImage} />
            </Paper>
            <Text size="sm">{option.label}</Text>
        </Group>
    )

    const handleSubmit = async (values) => {
        setLoading(true)
        
        const lvl1 = catLvl1.find(c => c.value === values.category_lvl1)?.label?.toLowerCase() || ''
        const lvl2 = catLvl2.find(c => c.value === values.category_lvl2)?.label?.toLowerCase() || ''
        const lvl3 = catLvl3.find(c => c.value === values.category_id)?.label?.toLowerCase() || ''

        const payload = {
            description: values.description,
            weight: Number(values.weight),
            weight_unit: values.weight_unit,
            qty_cbm: Number(values.qty_cbm),
            category_id: Number(values.category_id),
            category_format: `wholesale:${lvl1}:${lvl2}:${lvl3}:default`,
            hscode_ids: values.hscodes ? values.hscodes.map(Number) : []
        }

        try {
            let res
            if (dataDetail) {
                res = await updateCategoryRule(dataDetail.id, payload)
            } else {
                res = await createCategoryRule(payload)
            }

            if (res.success || res.data) {
                notifications.show({
                    title: 'Success',
                    message: `Category Rule has been ${dataDetail ? 'updated' : 'created'} successfully`,
                    color: 'teal',
                    icon: <IconCheck />,
                })
                onCloseModal()
                reloadList()
            } else {
                notifications.show({
                    title: 'Error',
                    message: res.message || 'Something went wrong',
                    color: 'red',
                    icon: <IconX />,
                })
            }
        } catch (error) {
            console.error('Submit error:', error)
            notifications.show({
                title: 'Error',
                message: error.response?.data?.message || 'Something went wrong',
                color: 'red',
                icon: <IconX />,
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box>
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Box mb="md">
                    <TextInput
                        label="Description"
                        placeholder="Enter description"
                        withAsterisk
                        {...form.getInputProps('description')}
                    />
                </Box>
                
                <SimpleGrid cols={3} mb="md">
                    <NumberInput
                        label="Weight"
                        placeholder="0"
                        min={0}
                        {...form.getInputProps('weight')}
                    />
                    <Select
                        label="Weight Unit"
                        data={[
                            { value: 'GR', label: 'Gram (GR)' },
                            { value: 'KG', label: 'Kilogram (KG)' }
                        ]}
                        {...form.getInputProps('weight_unit')}
                    />
                    <NumberInput
                        label="Quantity CBM"
                        placeholder="1"
                        min={0}
                        suffix=" pcs/cbm"
                        {...form.getInputProps('qty_cbm')}
                    />
                </SimpleGrid>

                <Box mb="md">
                    <Text fw={500} size="sm" mb="xs">Select Category</Text>
                    <SimpleGrid cols={3}>
                        <Select
                            placeholder="Category Level 1"
                            data={catLvl1}
                            searchable
                            renderOption={renderSelectOption}
                            {...form.getInputProps('category_lvl1')}
                            onChange={(val) => {
                                form.setFieldValue('category_lvl1', val)
                                form.setFieldValue('category_lvl2', null)
                                form.setFieldValue('category_id', null)
                                fetchCategoriesLvl2(val)
                            }}
                        />
                        <Select
                            placeholder="Category Level 2"
                            data={catLvl2}
                            disabled={!form.values.category_lvl1}
                            searchable
                            renderOption={renderSelectOption}
                            {...form.getInputProps('category_lvl2')}
                            onChange={(val) => {
                                form.setFieldValue('category_lvl2', val)
                                form.setFieldValue('category_id', null)
                                fetchCategoriesLvl3(val)
                            }}
                        />
                        <Select
                            placeholder="Category Level 3"
                            data={catLvl3}
                            disabled={!form.values.category_lvl2}
                            searchable
                            renderOption={renderSelectOption}
                            {...form.getInputProps('category_id')}
                        />
                    </SimpleGrid>
                    {form.errors.category_id && (
                        <Text c="red" size="xs" mt={5}>{form.errors.category_id}</Text>
                    )}
                </Box>

                <Paper withBorder p="sm" radius="md" mb="lg" bg="var(--mantine-color-gray-0)">
                    <Text fw={600} size="sm" mb="xs">HS Code Configuration</Text>
                    <Text size="xs" c="dimmed" mb="md">Filter by country to narrow down the available HS Codes, then select the applicable ones.</Text>
                    <SimpleGrid cols={2}>
                        <Select
                            label="Filter by Country"
                            placeholder="Select Country"
                            data={countries}
                            searchable
                            clearable
                            value={selectedCountry}
                            onChange={setSelectedCountry}
                        />
                        <MultiSelect
                            label="Select HS Codes"
                            placeholder="Choose HS Codes"
                            data={hscodes}
                            searchable
                            clearable
                            {...form.getInputProps('hscodes')}
                        />
                    </SimpleGrid>
                </Paper>

                <Flex justify="flex-end" gap="sm">
                    <Button variant="outline" color="gray" onClick={onCloseModal}>Cancel</Button>
                    <Button type="submit" loading={loading}>Save</Button>
                </Flex>
            </form>
        </Box>
    )
}

export default FormCategoryRule
